/**
 * A saved, job-specific RAMS assessment. Absence means unassessed: callers must
 * never turn library defaults or a partial editor form into a residual score.
 * Keys are catalogue slugs, custom-<account hazard id>, or custom_<project id>.
 * The generation core must resolve those identities in its authorized register.
 */
import { z } from "zod";
import { RamsRiskBaselineReviewV1Schema, RamsRiskBaselineReviewReadV1Schema } from "./risk-baselines";
import { RamsHazardEditsSchema, RamsHazardEditsReadSchema, RamsEffectiveHazardsSnapshotSchema, RamsEffectiveHazardsSnapshotReadSchema } from "./hazard-edits";

export const RamsHazardAssessmentSchema = z.object({
  name: z.string().trim().min(1).max(200),
  description: z.string().trim().max(2000),
  whoAtRisk: z.string().trim().min(1).max(2000),
  controls: z.string().trim().min(1).max(10000),
  ppeRequired: z.string().trim().min(1).max(4000),
  likelihood: z.number().int().min(1).max(5),
  severity: z.number().int().min(1).max(5),
  residualLikelihood: z.number().int().min(1).max(5),
  residualSeverity: z.number().int().min(1).max(5),
  confirmed: z.literal(true),
}).strict();

export type RamsHazardAssessment = z.infer<typeof RamsHazardAssessmentSchema>;

function assessmentMapSchema<T extends z.ZodType>(assessmentSchema: T) {
return z.preprocess((value, context) => {
  // Zod's record parser skips __proto__; inspect own input keys before it can
  // discard an authored property and falsely report an empty valid map.
  if (value && typeof value === "object" && !Array.isArray(value) &&
      Object.keys(value).some((key) => ["__proto__", "constructor", "prototype"].includes(key))) {
    context.addIssue({ code: "custom", message: "Invalid hazard identity." });
    return z.NEVER;
  }
  return value;
}, z.record(
  z.string().min(1).max(200).regex(/^[a-zA-Z0-9_-]+$/)
    .refine((key) => !["__proto__", "constructor", "prototype"].includes(key), "Invalid hazard identity."),
  assessmentSchema,
)).superRefine((value, context) => {
  if (Object.keys(value).length > 500 ||
      new TextEncoder().encode(JSON.stringify(value)).byteLength > 1024 * 1024) {
    context.addIssue({ code: "custom", message: "RAMS hazard assessments exceed the document limit." });
  }
});

}

/** Requests and persisted server records reject unknown assessment fields. */
export const RamsHazardAssessmentsSchema = assessmentMapSchema(RamsHazardAssessmentSchema);
/** Installed readers tolerate additive record fields while retaining all known
 * field constraints, identity checks and whole-map limits. */
export const RamsHazardAssessmentReadSchema = RamsHazardAssessmentSchema.strip();
export const RamsHazardAssessmentsReadSchema = assessmentMapSchema(RamsHazardAssessmentReadSchema);

export type RamsHazardAssessments = z.infer<typeof RamsHazardAssessmentsSchema>;

/** Incomplete editing state is durable, but can never be a generation assessment. */
export const RamsHazardAssessmentEditorDraftSchema = z.object({
  version: z.literal(1),
  slug: z.string().min(1).max(200).regex(/^[a-zA-Z0-9_-]+$/)
    .refine((key) => !["__proto__", "constructor", "prototype"].includes(key)),
  form: z.object({
    name: z.string().max(200), description: z.string().max(2000),
    whoAtRisk: z.string().max(2000), controls: z.string().max(10000),
    ppeRequired: z.string().max(4000),
    likelihood: z.string().regex(/^[1-5]?$/), severity: z.string().regex(/^[1-5]?$/),
    residualLikelihood: z.string().regex(/^[1-5]?$/), residualSeverity: z.string().regex(/^[1-5]?$/),
  }).strict(),
}).strict();
export type RamsHazardAssessmentEditorDraft = z.infer<typeof RamsHazardAssessmentEditorDraftSchema>;

/** Current RAMS answers after its autosave draft has been cleared on generation.
 * All response object layers tolerate additive fields. Requests and server egress
 * use their independent strict schemas so compatibility cannot relax writes. */
const ramsCurrentVersionFields = {
  sourceVersionId: z.string().uuid(),
  versionNumber: z.number().int().min(1),
  selectedHazardSlugs: z.array(z.string().trim().min(1)).max(500),
  unrecorded: z.array(z.enum(["selectedHazardSlugs", "hazardAssessments"])),
};
export const RamsRegenerationPrefillResponseV1Schema = z.object({
  ramsHazardEditsVersion:z.literal(1).optional(),
  ramsRiskBaselineReview:RamsRiskBaselineReviewReadV1Schema.optional(),
  ramsCurrentVersion: z.object({
    ...ramsCurrentVersionFields,
    hazardAssessments: RamsHazardAssessmentsReadSchema,
    hazardEdits:RamsHazardEditsReadSchema.optional(),
    effectiveHazards:RamsEffectiveHazardsSnapshotReadSchema.optional(),
  }).nullable(),
});
export const RamsRegenerationPrefillResponseV1Strict = z.object({
  ramsHazardEditsVersion:z.literal(1).optional(),
  ramsRiskBaselineReview:RamsRiskBaselineReviewV1Schema.optional(),
  ramsCurrentVersion: z.object({
    ...ramsCurrentVersionFields,
    hazardAssessments: RamsHazardAssessmentsSchema,
    hazardEdits:RamsHazardEditsSchema.optional(),
    effectiveHazards:RamsEffectiveHazardsSnapshotSchema.optional(),
  }).strict().nullable(),
}).strict();
export type RamsRegenerationPrefillResponseV1 = z.infer<typeof RamsRegenerationPrefillResponseV1Schema>;
